/* eslint-disable no-param-reassign */
import * as Sentry from '@sentry/react';
import React from 'react';
import App from '~/App';
import {
  EValidCardComponents,
  EValidLoanComponents,
  EValidLoanComponentsNL,
  Market,
  SupportedLocales,
} from '~/sharedInterfaces/SharedEnums';
import {
  IAvailableWidgetTypes,
  IConfig,
  IPage,
  ISettings,
} from '~/sharedInterfaces/SharedInterfaces';
import { getLocale } from '~/utils';
import { defaultPages } from './DefaultConfigurationsService';

interface IPageValidation {
  status: boolean;
  hasRequiredComponents: boolean;
  invalidComponents: string[];
  duplicateComponents: string[];
  checkedRequiredComponents: string[];
}

interface ISettingsValidation {
  status: boolean;
  validLocale: boolean;
  validWidgetType: boolean;
  nonEmptyPartnerCode: boolean;
}

export const setupSentryProduction = () => {
  if (process.env.NODE_ENV === 'production') {
    Sentry.init({
      dsn: 'https://f4e6f5062c0443e497a552076647fa23@o294555.ingest.sentry.io/5509750',
      normalizeDepth: 10,
      release: `Widget@${process.env.npm_package_version}`,
      ignoreErrors: [
        'hbspt',
        '$',
        'jQuery',
        'https://assets-global.website-files.com',
        'HubSpot',
        'fbq',
        'hsq',
        '_hsq',
        'weglot',
        'bootstrap',
      ],
      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /.*-extension:\/\//i,
        /^chrome:\/\//i,
      ],
      beforeBreadcrumb(breadcrumb, hint) {
        if (breadcrumb.category === 'xhr') {
          breadcrumb.data = { response: JSON.parse((hint?.xhr as XMLHttpRequest).response) };
        }
        return breadcrumb;
      },
      environment: process.env.NODE_ENV,
    });
  }
};

export const setupUrlParamsInTest = (settings: any, version: number) => {
  if (process.env.NODE_ENV !== 'production') {
    // Log out widget verion
    // eslint-disable-next-line no-console
    console.info('Widget version: ', version);

    // This gives the testers the ability to change url params in the format http://localhost:8080/?market=dk&language=da so that all markets can be tested in verify
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const market = urlParams.get('market');
    const lang = urlParams.get('language');
    if (market) settings.market = market;
    if (lang) settings.language = lang;
  }
};

const validateWidgetType = (widgetType: string): boolean => {
  return !!((widgetType && widgetType === 'card') || (widgetType && widgetType === 'loan'));
};

const validLocales = Object.values(SupportedLocales);

const validateSettings = (settings: ISettings): ISettingsValidation => {
  const locale = getLocale(settings);
  const validLocale = locale
    ? validLocales.some((availableLocale) => availableLocale === locale)
    : false;
  const validWidgetType = validateWidgetType(settings.widgetType);
  const nonEmptyPartnerCode = settings.partnerCode !== '';

  return {
    status: validLocale && validWidgetType && nonEmptyPartnerCode,
    validLocale,
    validWidgetType,
    nonEmptyPartnerCode,
  };
};

const availableWidgetTypes = {
  loan: EValidLoanComponents,
  card: EValidCardComponents,
};

const validComponents = (widgetType: string, market: string) => {
  if (market === Market.Netherlands && widgetType === 'loan') {
    return Object.values(EValidLoanComponentsNL);
  }
  return Object.values(availableWidgetTypes[widgetType as keyof IAvailableWidgetTypes<string>]);
};

const validatePages = (config: IConfig) => {
  const pageValidation: IPageValidation = {
    status: true,
    hasRequiredComponents: true,
    invalidComponents: [],
    duplicateComponents: [],
    checkedRequiredComponents: [],
  };

  if (!validateWidgetType(config.settings.widgetType)) {
    pageValidation.status = false;
    return pageValidation;
  }

  if (!config.pages) {
    pageValidation.status = true;
    return pageValidation;
  }

  /*
   Only enable validation of required components in NL right now,
   because we don't have any requirements to validate required components
   in the other markets in the initialization of the widget
  */
  if (config.settings.market === Market.Netherlands) {
    pageValidation.hasRequiredComponents = false;
  }

  const checkedComponents: string[] = [];

  config.pages.forEach((page) => {
    Object.keys(page).forEach((componentName) => {
      if (config.pages.some((configPage) => configPage[componentName as keyof IPage])) {
        if (
          componentName !== 'next' &&
          componentName !== 'previous' &&
          componentName !== 'text' &&
          componentName !== 'header' &&
          checkedComponents.some((checkedComponent) => checkedComponent === componentName)
        ) {
          pageValidation.status = false;
          pageValidation.duplicateComponents.push(componentName);
        }
        checkedComponents.push(componentName);
      }

      if (
        !validComponents(config.settings.widgetType, config.settings.market).some(
          (validComponent) => validComponent === componentName
        ) &&
        !pageValidation.duplicateComponents.some(
          (duplicatedComponent) => duplicatedComponent === componentName
        )
      ) {
        pageValidation.status = false;
        pageValidation.invalidComponents.push(componentName);
      }

      if (config.settings.market === Market.Netherlands && componentName === 'homeAddress') {
        pageValidation.hasRequiredComponents = true;
      }
    });
  });

  return pageValidation;
};

const validateConfig = (config: IConfig) => {
  const validSettings = validateSettings(config.settings);
  const validPages = validatePages(config);
  return {
    validConfig: validSettings.status && validPages.status && validPages.hasRequiredComponents,
    validSettings,
    validPages,
  };
};

export const validateConfigAndRenderContent = (config: IConfig) => {
  const configResult = validateConfig(config);

  if (configResult.validConfig) {
    return (
      <App
        pages={
          config.pages ||
          defaultPages(config.settings.market, config.settings.language, config.settings.widgetType)
        }
        settings={config.settings}
      />
    );
  }

  Sentry.withScope((scope) => {
    scope.setFingerprint(['Invalid configuration']);
    Sentry.captureException(new Error('Invalid configuration'), {
      extra: {
        configResult,
        config,
      },
    });
  });

  return (
    <>
      <h1>The Widget configuration contains invalid values</h1>
      {!configResult.validSettings.status && (
        <>
          {!configResult.validSettings.validLocale && (
            <>
              <h2>{`Current Locale: (${getLocale(config.settings)})`}</h2>
              <h3>Valid Locales</h3>
              <ul>
                {validLocales.map((locale) => (
                  <li key={locale}>{locale}</li>
                ))}
              </ul>
            </>
          )}
          {!validateWidgetType(config.settings.widgetType) && (
            <>
              <h2>{`Unrecognized widgetType: ${config.settings.widgetType}`}</h2>
              <p>Valid widget types:</p>
              <ul>
                <li>&ldquo;loan&ldquo;</li>
                <li>&ldquo;card&ldquo;</li>
              </ul>
            </>
          )}
        </>
      )}
      {!configResult.validPages.status && validateWidgetType(config.settings.widgetType) && (
        <>
          <h2>{`Configuration errors with widgetType: ${config.settings.widgetType}`}</h2>
          {configResult.validPages.invalidComponents.length !== 0 && (
            <>
              <h3>Invalid components</h3>
              <ul>
                {configResult.validPages.invalidComponents.map((component) => (
                  <li key={component}>{component}</li>
                ))}
              </ul>

              <h3>Valid components</h3>
              <ul>
                {validComponents(config.settings.widgetType, config.settings.market).map(
                  (component) => {
                    return <li key={component}>{component}</li>;
                  }
                )}
              </ul>
            </>
          )}

          {configResult.validPages.duplicateComponents.length !== 0 && (
            <>
              <h3>Duplicate components</h3>
              <ul>
                {configResult.validPages.duplicateComponents.map((component) => (
                  <li key={component}>{component}</li>
                ))}
              </ul>
            </>
          )}

          {!configResult.validSettings.nonEmptyPartnerCode && (
            <>
              <h3>Invalid Partner Code</h3>
              <span>Did you forget to add your Partner Code in the Widget Settings?</span>
            </>
          )}
        </>
      )}
      {!configResult.validPages.hasRequiredComponents && (
        <>
          <h3>Missing Required Components</h3>
          <span>Home Address Component Missing</span>
        </>
      )}
    </>
  );
};
